/**
 * ALTIORA Design System v1 — shared primitives that carry the Landing
 * DNA into every deep page. Import as :
 *
 *   import { PageShell, PageHeader, Card, Section, Pill,
 *              PrimaryButton, GhostButton, TABS_STYLE } from
 *              "@/components/altiora/PremiumUI";
 *
 * Design tokens EXACTLY match Landing.jsx.
 */
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useEffect, useId, useRef, useState } from "react";
import {
  ChevronRight, X, AlertCircle, CheckCircle2, Info,
  AlertTriangle, ChevronDown,
} from "lucide-react";
import {
  modalOverlay, modalPanel, accordionExpand, tableRow, staggerContainer,
  SKELETON_CSS, useSafeMotion,
} from "./motion";

// ── Tokens ─────────────────────────────────────────────────────────
export const NAVY       = "#0B2545";
export const NAVY_DARK  = "#061528";
export const CHAMPAGNE  = "#C5A880";
export const GOLD       = "#F5D687";
export const IVORY      = "#FBF6EA";
export const SLATE      = "#94A3B8";
export const GREEN_LEAF = "#7EE0AA";
export const RED_LEAF   = "#FCA5A5";

// ── Motion presets (from Landing) ──────────────────────────────────
export const fadeUp = {
  hidden:  { opacity: 0, y: 18 },
  visible: (i = 0) => ({ opacity: 1, y: 0,
                            transition: { delay: i * 0.05, duration: 0.45,
                                            ease: [0.2, 0.7, 0.2, 1] } }),
};

// ── PageShell — full-page background matching Landing IVORY ────────
export function PageShell({ children, testid }) {
  return (
    <div className="min-h-screen relative" data-testid={testid}
         style={{ background: IVORY }}>
      {/* Grain overlay (Landing signature) */}
      <div className="fixed inset-0 opacity-[0.04] pointer-events-none"
           style={{ backgroundImage:
             "radial-gradient(rgba(11,37,69,.35) 1px, transparent 1px)",
             backgroundSize: "3px 3px" }} />
      <div className="relative">{children}</div>
    </div>
  );
}

// ── PageHeader — eyebrow + title + subtitle + optional back link ───
export function PageHeader({ eyebrow, title, subtitle, onBack, actions, testid }) {
  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp}
                  className="mb-6" data-testid={testid}>
      {onBack && (
        <button onClick={onBack} data-testid={`${testid}-back`}
                className="text-[11px] mb-3 flex items-center gap-1 transition"
                style={{ color: SLATE }}>
          <ChevronRight size={11} className="rotate-180" /> Retour
        </button>
      )}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          {eyebrow && (
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold"
                 style={{ color: CHAMPAGNE }}>{eyebrow}</div>
          )}
          <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl mt-1
                            leading-tight" style={{ color: NAVY }}>
            {title}
          </h1>
          {subtitle && (
            <p className="text-[13px] mt-2 max-w-2xl" style={{ color: SLATE }}>
              {subtitle}
            </p>
          )}
        </div>
        {actions && <div className="flex gap-2 items-center">{actions}</div>}
      </div>
    </motion.div>
  );
}

// ── Card — premium ivory card with subtle border + inner glow ──────
export function Card({ children, className = "", testid, elevated, style = {}, ...rest }) {
  // Accepte `testid` OU `data-testid` (compatibilité descendante + nouveau usage standard)
  const dt = rest["data-testid"] || testid;
  const extra = { ...rest };
  delete extra["data-testid"];
  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp}
                  data-testid={dt}
                  className={`rounded-2xl bg-white ${className}`}
                  style={{ border: `1px solid ${CHAMPAGNE}22`,
                             boxShadow: elevated
                               ? "0 10px 40px -20px rgba(11,37,69,0.20)"
                               : "0 2px 8px -3px rgba(11,37,69,0.06)",
                             ...style }}
                  {...extra}>
      {children}
    </motion.div>
  );
}

// ── Section — eyebrow + title + inline actions ─────────────────────
export function Section({ icon: I, title, hint, actions, children,
                              className = "", testid }) {
  return (
    <div className={className} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        {I && <I size={16} style={{ color: CHAMPAGNE }} />}
        <h3 className="font-serif text-lg leading-tight" style={{ color: NAVY }}>
          {title}
        </h3>
        {actions && <div className="ml-auto flex gap-1">{actions}</div>}
      </div>
      {hint && (
        <p className="text-[11px] mb-3" style={{ color: SLATE }}>{hint}</p>
      )}
      {children}
    </div>
  );
}

// ── Pill — small status/label chip ─────────────────────────────────
export function Pill({ children, tone = "neutral", testid }) {
  const tones = {
    neutral: { bg: "#F1F5F9", fg: "#334155" },
    gold:    { bg: `${GOLD}44`, fg: "#78500C" },
    green:   { bg: `${GREEN_LEAF}44`, fg: "#0B5A2C" },
    amber:   { bg: "#FEF3C7", fg: "#B45309" },
    red:     { bg: `${RED_LEAF}44`, fg: "#7F1D1D" },
    navy:    { bg: `${NAVY}0F`, fg: NAVY },
  }[tone] || { bg: "#F1F5F9", fg: "#334155" };
  return (
    <span data-testid={testid}
          className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
          style={{ background: tones.bg, color: tones.fg, fontWeight: 600 }}>
      {children}
    </span>
  );
}

// ── Buttons ────────────────────────────────────────────────────────
export function PrimaryButton({ children, onClick, disabled, testid, className = "" }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
            className={`text-[13px] px-4 py-2 rounded-md inline-flex items-center
                            gap-1.5 transition disabled:opacity-50 hover:opacity-90 ${className}`}
            style={{ background: CHAMPAGNE, color: NAVY_DARK, fontWeight: 600 }}>
      {children}
    </button>
  );
}


export function GhostButton({ children, onClick, disabled, testid, className = "" }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
            className={`text-[13px] px-3 py-2 rounded-md inline-flex items-center
                            gap-1.5 transition disabled:opacity-50 ${className}`}
            style={{ border: `1px solid ${CHAMPAGNE}55`, color: NAVY,
                       background: "transparent" }}>
      {children}
    </button>
  );
}

// ── Tabs style (used by pages with tab navigation) ─────────────────
export const TabsShell = ({ tabs, active, onChange, testid }) => (
  <div className="flex gap-1 p-1 rounded-lg bg-white shadow-sm mb-5 overflow-x-auto"
       data-testid={testid}
       style={{ border: `1px solid ${CHAMPAGNE}22` }}>
    {tabs.map(t => {
      const T = t.icon; const on = active === t.id;
      return (
        <button key={t.id} onClick={() => onChange(t.id)}
                data-testid={`${testid}-${t.id}`}
                className="text-[12px] px-3 py-2 rounded-md inline-flex items-center
                              gap-1.5 whitespace-nowrap transition"
                style={{ background: on ? NAVY : "transparent",
                           color:      on ? "#fff" : NAVY,
                           fontWeight: on ? 600 : 500 }}>
          {T && <T size={13} />} {t.label}
        </button>
      );
    })}
  </div>
);

// ── Metric — number + label ────────────────────────────────────────
export function Metric({ label, value, hint, accent, testid }) {
  return (
    <Card className="p-4" testid={testid}>
      <div className="font-serif text-2xl font-bold"
           style={{ color: accent || NAVY }}>{value}</div>
      <div className="text-[11px] mt-0.5 uppercase tracking-widest"
           style={{ color: SLATE }}>{label}</div>
      {hint && (
        <div className="text-[10px] mt-1" style={{ color: SLATE }}>{hint}</div>
      )}
    </Card>
  );
}


// ══════════════════════════════════════════════════════════════════
// Design System · Phase 0 · New primitives (WCAG 2.2 AA)
// Every primitive :
//   · has a data-testid
//   · uses shared motion presets (motion.js) — no ad-hoc animation
//   · is keyboard-navigable + focus-visible-friendly
//   · respects prefers-reduced-motion via useSafeMotion
// ══════════════════════════════════════════════════════════════════

// ── Divider ────────────────────────────────────────────────────────
export function Divider({ testid, className = "" }) {
  return (
    <hr data-testid={testid}
        className={`border-0 h-px my-4 ${className}`}
        style={{ background: `${CHAMPAGNE}33` }} />
  );
}

// ── Loader (accessible, respects reduced motion) ───────────────────
export function Loader({ size = 18, label = "Chargement…", testid }) {
  return (
    <span role="status" aria-live="polite" data-testid={testid}
          className="inline-flex items-center gap-2">
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
           className="animate-spin motion-reduce:animate-none" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke={`${CHAMPAGNE}55`} strokeWidth="3" />
        <path d="M22 12a10 10 0 0 1-10 10" stroke={NAVY} strokeWidth="3"
              strokeLinecap="round" />
      </svg>
      <span className="sr-only-altiora">{label}</span>
    </span>
  );
}

// ── Skeleton (shimmer) ─────────────────────────────────────────────
export function Skeleton({ className = "", height = 12, width = "100%", testid,
                              rounded = "md" }) {
  const r = { sm: 4, md: 8, lg: 12, full: 9999 }[rounded] || 8;
  return (
    <div role="presentation" aria-hidden="true" data-testid={testid}
         className={className}
         style={{ height, width, borderRadius: r, ...SKELETON_CSS }} />
  );
}

// ── SkeletonCard — mimics a Card layout with title + 3 body lines
export function SkeletonCard({ testid, lines = 3 }) {
  return (
    <div className="rounded-xl bg-white p-5 space-y-2"
             style={{ border: `1px solid ${CHAMPAGNE}22` }}
             role="presentation" aria-hidden="true"
             data-testid={testid}>
      <Skeleton height={16} width="45%" />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} height={10} width={`${88 - i * 8}%`} />
      ))}
    </div>
  );
}

// ── SkeletonList — n rows of variable width lines
export function SkeletonList({ testid, rows = 5 }) {
  return (
    <div className="space-y-3" data-testid={testid}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton height={32} width={32} rounded="full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton height={12} width="40%" />
            <Skeleton height={10} width="70%" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── SkeletonTable — mimics a Table with n rows × m columns
export function SkeletonTable({ testid, rows = 4, cols = 4 }) {
  return (
    <div className="overflow-x-auto rounded-xl bg-white"
             style={{ border: `1px solid ${CHAMPAGNE}22` }}
             data-testid={testid}>
      <div className="grid gap-3 px-4 py-3"
              style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                          background: IVORY, borderBottom: `1px solid ${CHAMPAGNE}33` }}>
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} height={10} width="50%" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="grid gap-3 px-4 py-3"
                style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                            borderBottom: r < rows - 1 ? `1px solid ${CHAMPAGNE}18` : "none" }}>
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} height={12} width={c === 0 ? "80%" : "60%"} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Badge (semantic) — accessible variant of Pill ──────────────────
export function Badge({ children, variant = "neutral", size = "md", icon: I, testid }) {
  const variants = {
    neutral: { bg: "#F1F5F9",         fg: "#334155" },
    navy:    { bg: `${NAVY}0F`,       fg: NAVY },
    gold:    { bg: `${GOLD}44`,       fg: "#78500C" },
    green:   { bg: `${GREEN_LEAF}44`, fg: "#0B5A2C" },
    amber:   { bg: "#FEF3C7",         fg: "#B45309" },
    red:     { bg: `${RED_LEAF}44`,   fg: "#7F1D1D" },
    info:    { bg: "#E0F2FE",         fg: "#0369A1" },
    solid:   { bg: NAVY,              fg: "#fff" },
  }[variant] || { bg: "#F1F5F9", fg: "#334155" };
  const sizes = {
    sm: "text-[9px] px-1.5 py-[1px]",
    md: "text-[10px] px-2 py-0.5",
    lg: "text-[11px] px-2.5 py-1",
  }[size];
  return (
    <span data-testid={testid}
          className={`inline-flex items-center gap-1 uppercase tracking-widest
                          rounded-full ${sizes}`}
          style={{ background: variants.bg, color: variants.fg, fontWeight: 600 }}>
      {I && <I size={10} aria-hidden="true" />} {children}
    </span>
  );
}

// ── EmptyState ─────────────────────────────────────────────────────
export function EmptyState({ icon: I, title, description, action, testid }) {
  return (
    <div role="status" aria-live="polite" data-testid={testid}
         className="flex flex-col items-center justify-center text-center py-14 px-6">
      {I && <div className="mb-3 p-3 rounded-full"
                    style={{ background: `${CHAMPAGNE}22`, color: NAVY }}>
        <I size={22} aria-hidden="true" /></div>}
      <h3 className="font-serif text-lg" style={{ color: NAVY }}>{title}</h3>
      {description && (
        <p className="text-[13px] mt-2 max-w-md" style={{ color: SLATE }}>
          {description}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ── Alert (semantic, dismissible-optional) ─────────────────────────
export function Alert({ variant = "info", title, children, onDismiss, testid }) {
  const map = {
    info:    { icon: Info,           bg: "#E0F2FE", fg: "#0369A1", bd: "#0369A122" },
    success: { icon: CheckCircle2,   bg: "#DCFCE7", fg: "#166534", bd: "#16653422" },
    warning: { icon: AlertTriangle,  bg: "#FEF3C7", fg: "#92400E", bd: "#92400E22" },
    danger:  { icon: AlertCircle,    bg: "#FEE2E2", fg: "#991B1B", bd: "#991B1B22" },
  }[variant] || {};
  const I = map.icon;
  return (
    <div role="alert" data-testid={testid}
         className="rounded-lg p-3 flex items-start gap-2"
         style={{ background: map.bg, border: `1px solid ${map.bd}` }}>
      {I && <I size={16} style={{ color: map.fg }} className="mt-0.5 shrink-0" aria-hidden="true" />}
      <div className="flex-1 min-w-0">
        {title && <div className="text-[13px] font-semibold" style={{ color: map.fg }}>{title}</div>}
        {children && <div className="text-[12px] mt-0.5" style={{ color: map.fg, opacity: 0.9 }}>{children}</div>}
      </div>
      {onDismiss && (
        <button onClick={onDismiss} aria-label="Fermer"
                data-testid={testid ? `${testid}-dismiss` : undefined}
                className="p-1 rounded hover:opacity-80"
                style={{ color: map.fg }}>
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

// ── Label ─────────────────────────────────────────────────────────
export function Label({ htmlFor, children, required, hint, testid }) {
  return (
    <label htmlFor={htmlFor} data-testid={testid}
           className="block text-[11px] uppercase tracking-widest font-semibold mb-1"
           style={{ color: NAVY }}>
      {children}
      {required && <span aria-label="requis" style={{ color: "#B91C1C" }}> *</span>}
      {hint && (
        <span className="ml-2 normal-case tracking-normal font-normal text-[11px]"
              style={{ color: SLATE }}>{hint}</span>
      )}
    </label>
  );
}

// ── FormField wrapper — auto-labels, error, description ────────────
export function FormField({ label, required, hint, error, description, children,
                                id: idProp, testid }) {
  const autoId = useId();
  const id = idProp || autoId;
  const descId = description ? `${id}-desc` : undefined;
  const errId  = error       ? `${id}-err`  : undefined;
  const child  = typeof children === "function"
    ? children({ id, "aria-describedby": [descId, errId].filter(Boolean).join(" ") || undefined,
                     "aria-invalid": error ? true : undefined })
    : children;
  return (
    <div data-testid={testid} className="mb-3">
      {label && <Label htmlFor={id} required={required} hint={hint}>{label}</Label>}
      {child}
      {description && (
        <div id={descId} className="text-[11px] mt-1" style={{ color: SLATE }}>
          {description}
        </div>
      )}
      {error && (
        <div id={errId} role="alert" className="text-[11px] mt-1"
             style={{ color: "#B91C1C" }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ── Input · Textarea · Select · Checkbox ───────────────────────────
const inputBase = {
  border:      `1px solid ${CHAMPAGNE}55`,
  background:  "#fff",
  borderRadius: 8,
  padding:     "10px 12px",
  fontSize:    13,
  color:       NAVY,
  width:       "100%",
  outline:     "none",
  transition:  "border-color 150ms cubic-bezier(0.2,0.7,0.2,1), box-shadow 150ms",
};

export function Input({ testid, className = "", ...props }) {
  return (
    <input data-testid={testid} className={className}
              style={inputBase} {...props} />
  );
}

export function Textarea({ testid, className = "", rows = 4, ...props }) {
  return (
    <textarea data-testid={testid} className={`resize-none ${className}`}
                  style={inputBase} rows={rows} {...props} />
  );
}

export function Select({ testid, className = "", children, ...props }) {
  return (
    <div className="relative">
      <select data-testid={testid}
              className={`appearance-none pr-8 ${className}`}
              style={inputBase} {...props}>
        {children}
      </select>
      <ChevronDown size={13} aria-hidden="true"
        className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ color: SLATE }} />
    </div>
  );
}

export function Checkbox({ label, checked, onChange, testid, id: idProp, disabled }) {
  const autoId = useId(); const id = idProp || autoId;
  return (
    <label htmlFor={id} className="inline-flex items-start gap-2 cursor-pointer select-none">
      <input id={id} type="checkbox" data-testid={testid}
             checked={!!checked} onChange={onChange} disabled={disabled}
             className="mt-0.5 w-4 h-4 rounded"
             style={{ accentColor: NAVY }} />
      {label && <span className="text-[13px]" style={{ color: NAVY }}>{label}</span>}
    </label>
  );
}

// ── Modal — accessible dialog with focus trap + ESC + backdrop close ─
export function Modal({ open, onClose, title, description, children, footer,
                            size = "md", testid, closeOnBackdrop = true }) {
  const overlay = useSafeMotion(modalOverlay);
  const panel   = useSafeMotion(modalPanel);
  const dialogRef = useRef(null);
  const titleId = useId(); const descId = useId();

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement;
    const onKey = (e) => {
      if (e.key === "Escape") { onClose?.(); }
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0], last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    setTimeout(() => dialogRef.current?.querySelector("[data-autofocus], button, input, textarea, select")?.focus(), 30);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open, onClose]);

  const widths = { sm: 400, md: 560, lg: 760, xl: 960 };

  return (
    <AnimatePresence>
      {open && (
        <motion.div key="ov" {...overlay} data-testid={`${testid}-overlay`}
          onClick={() => closeOnBackdrop && onClose?.()}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          style={{ background: "rgba(6,21,40,0.55)", backdropFilter: "blur(6px)" }}>
          <motion.div key="p" {...panel}
            ref={dialogRef} role="dialog" aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            aria-describedby={description ? descId : undefined}
            data-testid={testid}
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded-2xl bg-white shadow-2xl overflow-hidden"
            style={{ maxWidth: widths[size] || 560,
                       border: `1px solid ${CHAMPAGNE}33` }}>
            <div className="flex items-start gap-3 p-5 border-b"
                    style={{ borderColor: `${CHAMPAGNE}22` }}>
              <div className="flex-1 min-w-0">
                {title && (
                  <h2 id={titleId} className="font-serif text-xl leading-tight"
                          style={{ color: NAVY }}>{title}</h2>
                )}
                {description && (
                  <p id={descId} className="text-[12px] mt-1" style={{ color: SLATE }}>
                    {description}
                  </p>
                )}
              </div>
              <button onClick={onClose} aria-label="Fermer la fenêtre"
                          data-testid={`${testid}-close`}
                          className="p-1 rounded hover:opacity-80"
                          style={{ color: SLATE }}>
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <div className="p-5 max-h-[70vh] overflow-y-auto">{children}</div>
            {footer && (
              <div className="p-4 border-t flex gap-2 justify-end"
                       style={{ borderColor: `${CHAMPAGNE}22`, background: IVORY }}>
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Table — accessible, animated rows ──────────────────────────────
export function Table({ columns, rows, empty = "Aucune donnée à afficher.",
                            caption, testid }) {
  const container = useSafeMotion(staggerContainer);
  const row = useSafeMotion(tableRow);
  if (!rows?.length) {
    return <EmptyState title={empty} testid={testid ? `${testid}-empty` : undefined} />;
  }
  return (
    <div className="overflow-x-auto rounded-xl bg-white"
             style={{ border: `1px solid ${CHAMPAGNE}22` }}
             data-testid={testid}>
      <table className="w-full text-[13px]" role="table">
        {caption && <caption className="sr-only-altiora">{caption}</caption>}
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c.key} scope="col"
                    className="text-left text-[10px] uppercase tracking-widest px-3 py-2 font-semibold"
                    style={{ color: SLATE, background: IVORY,
                               borderBottom: `1px solid ${CHAMPAGNE}33` }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <motion.tbody {...container}>
          {rows.map((r, i) => (
            <motion.tr key={r.id ?? i} {...row}
                          data-testid={testid ? `${testid}-row-${i}` : undefined}
                          className="hover:bg-slate-50 transition-colors"
                          style={{ borderBottom: `1px solid ${CHAMPAGNE}18` }}>
              {columns.map(c => (
                <td key={c.key} className="px-3 py-2.5" style={{ color: NAVY }}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </motion.tr>
          ))}
        </motion.tbody>
      </table>
    </div>
  );
}

// ── Accordion ──────────────────────────────────────────────────────
export function Accordion({ items, testid }) {
  const [open, setOpen] = useState(null);
  const anim = useSafeMotion(accordionExpand);
  return (
    <div className="rounded-xl bg-white" style={{ border: `1px solid ${CHAMPAGNE}22` }}
             data-testid={testid}>
      {items.map((it, i) => {
        const isOpen = open === i;
        return (
          <div key={i} style={{ borderBottom: i < items.length - 1
                                                     ? `1px solid ${CHAMPAGNE}22` : "none" }}>
            <button onClick={() => setOpen(isOpen ? null : i)}
                        aria-expanded={isOpen}
                        aria-controls={`${testid}-panel-${i}`}
                        data-testid={testid ? `${testid}-header-${i}` : undefined}
                        className="w-full flex items-center gap-2 p-4 text-left transition hover:bg-slate-50">
              <ChevronDown size={14} aria-hidden="true"
                style={{ color: CHAMPAGNE,
                              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 200ms" }} />
              <span className="text-[13px] font-semibold" style={{ color: NAVY }}>
                {it.title}
              </span>
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div id={`${testid}-panel-${i}`} role="region"
                                {...anim} className="overflow-hidden">
                  <div className="p-4 pt-0 text-[12px]" style={{ color: SLATE }}>
                    {it.content}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

// ── Tooltip (accessible; hover + focus) ────────────────────────────
export function Tooltip({ label, children, side = "top", testid }) {
  const [show, setShow] = useState(false);
  const id = useId();
  const positions = {
    top:    "bottom-full left-1/2 -translate-x-1/2 mb-1.5",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-1.5",
    left:   "right-full top-1/2 -translate-y-1/2 mr-1.5",
    right:  "left-full top-1/2 -translate-y-1/2 ml-1.5",
  };
  return (
    <span className="relative inline-flex" data-testid={testid}
              onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}
              onFocus={() => setShow(true)}       onBlur={() => setShow(false)}
              aria-describedby={show ? id : undefined}>
      {children}
      {show && (
        <span id={id} role="tooltip"
              className={`absolute z-[100] whitespace-nowrap text-[11px] px-2 py-1
                              rounded pointer-events-none ${positions[side]}`}
              style={{ background: NAVY, color: "#fff" }}>
          {label}
        </span>
      )}
    </span>
  );
}

// ── DangerButton · IconButton — complete the button family ─────────
export function DangerButton({ children, onClick, disabled, testid, className = "" }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
                className={`text-[13px] px-4 py-2 rounded-md inline-flex items-center
                                gap-1.5 transition disabled:opacity-50 hover:opacity-90 ${className}`}
                style={{ background: "#B91C1C", color: "#fff", fontWeight: 600 }}>
      {children}
    </button>
  );
}

export function IconButton({ icon: I, label, onClick, testid, tone = "neutral",
                                  size = 32, disabled }) {
  const tones = {
    neutral: { bg: "transparent", fg: NAVY,     border: `1px solid ${CHAMPAGNE}55` },
    solid:   { bg: NAVY,          fg: "#fff",   border: "none" },
    ghost:   { bg: "transparent", fg: NAVY,     border: "none" },
    gold:    { bg: CHAMPAGNE,     fg: NAVY_DARK, border: "none" },
  }[tone];
  return (
    <button onClick={onClick} aria-label={label} title={label}
                disabled={disabled} data-testid={testid}
                className="inline-flex items-center justify-center rounded-md transition
                              hover:opacity-90 disabled:opacity-50"
                style={{ width: size, height: size, ...tones }}>
      <I size={Math.round(size * 0.5)} aria-hidden="true" />
    </button>
  );
}

// ── Breadcrumbs (semantic nav) ─────────────────────────────────────
export function Breadcrumbs({ items, testid }) {
  return (
    <nav aria-label="Fil d'Ariane" data-testid={testid}>
      <ol className="flex items-center flex-wrap gap-1 text-[11px]"
              style={{ color: SLATE }}>
        {items.map((it, i) => (
          <li key={i} className="inline-flex items-center gap-1">
            {i > 0 && <ChevronRight size={11} aria-hidden="true" style={{ color: CHAMPAGNE }} />}
            {it.href ? (
              <a href={it.href} className="hover:underline transition"
                    style={{ color: i === items.length - 1 ? NAVY : SLATE,
                                fontWeight: i === items.length - 1 ? 600 : 400 }}
                    aria-current={i === items.length - 1 ? "page" : undefined}>
                {it.label}
              </a>
            ) : (
              <span style={{ color: i === items.length - 1 ? NAVY : SLATE,
                                 fontWeight: i === items.length - 1 ? 600 : 400 }}
                    aria-current={i === items.length - 1 ? "page" : undefined}>
                {it.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ── StatCard — the same tile used on Landing "cost" section ────────
export function StatCard({ value, label, hint, prefix = "", suffix = "",
                              accent = "gold", testid }) {
  const colors = { gold: GOLD, navy: NAVY, champagne: CHAMPAGNE }[accent] || GOLD;
  return (
    <Card className="p-8 text-center" testid={testid}>
      <div className="font-serif" style={{ color: colors,
              fontSize: "clamp(2.2rem, 3.6vw, 3.4rem)",
              lineHeight: 1, fontWeight: 400 }}>
        {prefix}{value}{suffix}
      </div>
      <div className="text-[11px] mt-3 uppercase tracking-[0.24em]"
              style={{ color: SLATE }}>{label}</div>
      {hint && <div className="text-[11px] mt-2" style={{ color: SLATE }}>{hint}</div>}
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════════
// KanbanBoard · KanbanCard — Sales OS pipeline primitives (Sprint 1)
// HTML5 native drag & drop, no external dependency, accessible.
// Config-driven: caller supplies columns + cards + onMove callback.
// ══════════════════════════════════════════════════════════════════

/**
 * KanbanCard — draggable card for the KanbanBoard.
 *
 * Props:
 *   card         { id, title, subtitle, amount?, probability?, tags?, ... }
 *   onClick      ()               → open detail
 *   onDragStart  (cardId) => void
 *   currency     "CAD" (default)
 */
export function KanbanCard({ card, onClick, onDragStart, currency = "CAD", testid }) {
  const flagged = !!card.ai_blockage;
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", card.id);
        e.dataTransfer.effectAllowed = "move";
        onDragStart?.(card.id);
      }}
      onClick={onClick}
      data-testid={testid || `kanban-card-${card.id}`}
      className="cursor-grab active:cursor-grabbing rounded-lg bg-white p-3 mb-2
                     hover:shadow-md transition-shadow"
      style={{ border: `1px solid ${flagged ? "#F59E0B55" : `${CHAMPAGNE}33`}`,
                 borderLeft: `3px solid ${flagged ? "#F59E0B" : CHAMPAGNE}` }}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-[13px] font-semibold leading-tight" style={{ color: NAVY }}>
          {card.title}
        </div>
        {typeof card.probability === "number" && (
          <span className="text-[10px] shrink-0 px-1.5 py-[1px] rounded-full"
                style={{ background: `${NAVY}0F`, color: NAVY, fontWeight: 600 }}>
            {card.probability}%
          </span>
        )}
      </div>
      {card.subtitle && (
        <div className="text-[11px] mb-2" style={{ color: SLATE }}>{card.subtitle}</div>
      )}
      {typeof card.amount === "number" && card.amount > 0 && (
        <div className="text-[11px] font-semibold" style={{ color: CHAMPAGNE }}>
          {card.amount.toLocaleString("fr-CA", { style: "currency", currency,
                                                          maximumFractionDigits: 0 })}
        </div>
      )}
      {card.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {card.tags.map((t, i) => (
            <span key={i} className="text-[9px] uppercase tracking-widest px-1.5 py-[1px] rounded"
                  style={{ background: `${CHAMPAGNE}22`, color: NAVY, fontWeight: 600 }}>
              {t}
            </span>
          ))}
        </div>
      )}
      {flagged && (
        <div className="mt-1.5 text-[10px] leading-snug" style={{ color: "#B45309" }}>
          <AlertTriangle size={10} className="inline mr-1" aria-hidden="true" />
          {card.ai_blockage.slice(0, 90)}{card.ai_blockage.length > 90 ? "…" : ""}
        </div>
      )}
    </div>
  );
}

/**
 * KanbanBoard — horizontal scrollable pipeline.
 *
 * Props:
 *   columns      [{ key, label, color, count, total_commission,
 *                     is_won?, is_lost?, cards: [...] }]
 *   onMove       (cardId, toStageKey) => void   (called on drop)
 *   onCardClick  (card, column) => void
 *   currency     "CAD"
 *   emptyMessage "Aucune opportunité"
 */
export function KanbanBoard({ columns, onMove, onCardClick, currency = "CAD",
                                    emptyMessage = "Aucune opportunité", testid }) {
  const [hoverKey, setHoverKey] = useState(null);
  return (
    <div data-testid={testid}
         className="flex gap-3 overflow-x-auto pb-3"
         style={{ scrollSnapType: "x mandatory" }}>
      {columns.map((col) => {
        const isHover = hoverKey === col.key;
        return (
          <div key={col.key} data-testid={`${testid}-col-${col.key}`}
               onDragOver={(e) => { e.preventDefault(); setHoverKey(col.key); }}
               onDragLeave={() => setHoverKey(null)}
               onDrop={(e) => {
                 e.preventDefault();
                 const id = e.dataTransfer.getData("text/plain");
                 setHoverKey(null);
                 if (id) onMove?.(id, col.key);
               }}
               className="shrink-0 w-72 rounded-xl transition-colors"
               style={{
                 background: isHover ? `${col.color || CHAMPAGNE}18` : IVORY,
                 border: `1px solid ${col.color || CHAMPAGNE}33`,
                 scrollSnapAlign: "start",
               }}>
            {/* Column header */}
            <div className="px-3 py-2.5 flex items-center justify-between"
                 style={{ borderBottom: `1px solid ${col.color || CHAMPAGNE}22` }}>
              <div className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-4 rounded-sm"
                      style={{ background: col.color || CHAMPAGNE }} aria-hidden="true" />
                <span className="text-[11px] uppercase tracking-widest font-semibold"
                      style={{ color: NAVY }}>{col.label}</span>
                <span className="text-[10px] px-1.5 py-[1px] rounded-full"
                      style={{ background: `${NAVY}0F`, color: NAVY, fontWeight: 600 }}>
                  {col.count ?? col.cards?.length ?? 0}
                </span>
              </div>
              {typeof col.total_commission === "number" && col.total_commission > 0 && (
                <span className="text-[10px] font-semibold" style={{ color: CHAMPAGNE }}>
                  {col.total_commission.toLocaleString("fr-CA", {
                    style: "currency", currency, maximumFractionDigits: 0,
                  })}
                </span>
              )}
            </div>
            {/* Cards */}
            <div className="p-2 min-h-[100px]">
              {(col.cards || []).length === 0 ? (
                <div className="text-[11px] italic py-6 text-center"
                     style={{ color: SLATE }}>
                  {emptyMessage}
                </div>
              ) : (
                col.cards.map((card) => (
                  <KanbanCard key={card.id} card={card} currency={currency}
                              onClick={() => onCardClick?.(card, col)}
                              testid={`${testid}-card-${card.id}`} />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Re-export motion utilities & tokens for one-stop-shop consumers
export { fadeUp as fadeUpPreset, useSafeMotion } from "./motion";
export { COLOR, SEMANTIC, TYPO, SPACE, RADIUS, SHADOW, Z, BP } from "./tokens";
